<script setup>
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useRouter } from "vue-router";

import { store } from "@/store";
import {
  EMPTY_VALUE,
  capitalize,
  isBlank,
  numberFormatter,
} from "@/utils/formatters";

import AppButton from "@/components/AppButton";
import AppCard from "@/components/AppCard";
import AppInvoice from "@/components/AppInvoice.vue";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { id } = route.params;
const typeInvoice = ref("not_paid");
const countInvoicesToPay = ref(0);

import useApi from "@/composables/useApi";

const { service, invoice } = useApi();

const PENDING_STATES = ["not_paid", "partial"];
const PAID_STATES = ["paid", "reversed"];

const user = ref({});
const serviceData = ref({});
const invoiceData = ref([]);

const listInvoices = ref([]);

const allInvoices = computed(() =>
  Array.isArray(invoiceData.value) ? invoiceData.value : []
);

const pendingInvoices = computed(() =>
  allInvoices.value.filter((item) =>
    PENDING_STATES.includes(item.payment_state)
  )
);

const paidInvoices = computed(() =>
  allInvoices.value.filter((item) => PAID_STATES.includes(item.payment_state))
);

const pendingAmount = computed(() =>
  pendingInvoices.value.reduce(
    (total, item) => total + (Number(item.amount_residual) || 0),
    0
  )
);

const invoiceTypes = computed(() => [
  {
    value: "not_paid",
    label: t("invoices.not_payed"),
    icon: "uil uil-clock",
    count: pendingInvoices.value.length,
  },
  {
    value: "paid",
    label: t("invoices.paid"),
    icon: "uil uil-check-circle",
    count: paidInvoices.value.length,
  },
]);

const serviceStatus = computed(() =>
  typeof serviceData.value?.estatus === "string"
    ? serviceData.value.estatus.toLowerCase()
    : ""
);

const serviceVariant = computed(() => {
  const variants = {
    activo: "success",
    gratis: "success",
    suspendido: "warning",
    cancelado: "danger",
    inactivo: "danger",
  };

  return variants[serviceStatus.value] || "neutral";
});

const serviceIcon = computed(() =>
  serviceVariant.value === "success" ? "uil-wifi" : "uil-wifi-slash"
);

const serviceStatusLabel = computed(() =>
  serviceStatus.value ? capitalize(serviceStatus.value) : EMPTY_VALUE
);

const contractNumber = computed(() => {
  if (!isBlank(serviceData.value?.no_contrato)) {
    return serviceData.value.no_contrato;
  }

  // The service record may not carry the contract number: the invoices label
  // their `servicio_id` many2one with it, so use that as a fallback
  const labelled = allInvoices.value.find(
    (item) => Array.isArray(item.servicio_id) && item.servicio_id[1]
  );

  return labelled ? labelled.servicio_id[1] : EMPTY_VALUE;
});

const planName = computed(() => {
  const planId = serviceData.value?.plan_id;
  return Array.isArray(planId) && planId[1] ? planId[1] : "";
});

const getService = async (id) => {
  const data = await service.getServiceById(id);
  // search_read always answers with a list, even when limited to one record
  return Array.isArray(data.payload) ? data.payload[0] : data.payload;
};

const getInvoicesByService = async (service_id) => {
  const data = await invoice.listInvoicesByService(service_id);
  return data.payload;
};

const loadPaymentHistory = async (service_id) => {
  const data = await invoice.getPaymentHistoryInvoicesByService(service_id);
  return data;
};

const filterInvoices = async () => {
  const payment_history = await loadPaymentHistory(id);
  if (typeInvoice.value === "paid") {
    listInvoices.value = paidInvoices.value.slice();
  } else {
    listInvoices.value = pendingInvoices.value.slice();
  }
  listInvoices.value.sort((a, b) => {
    // First sort by due date descending
    const dateCompare =
      new Date(b.invoice_date_due) - new Date(a.invoice_date_due);
    if (dateCompare !== 0) return dateCompare;
    // If dates are equal, sort by id ascending
    return a.id - b.id;
  });
  // listInvoices.value = listInvoices.value.filter(invoice => !payment_history.includes(invoice.id));
  listInvoices.value = listInvoices.value.map((invoice) => ({
    ...invoice,
    payment_status: payment_history.includes(invoice.id) ? true : false,
  }));
};

const selectType = async (value) => {
  if (typeInvoice.value === value) {
    return;
  }

  typeInvoice.value = value;
  await filterInvoices();
};

const getCountInvoicesPendingByService = async (service_id) => {
  const data = await invoice.countInvoicesPendingByService(service_id);
  return data.payload;
};

const goQuickPayment = () => {
  console.log("quick payment");
  const service_id = serviceData.value.name;
  router.push({ name: "QuickPaymentInvoices", params: { id, service_id } });
};

const goInvoiceSubscription = () => {
  console.log("invoice subscription");
  const selected_invoice = listInvoices.value.find(
    (invoice) => invoice.payment_state === "not_paid"
  );
  console.log("selected_invoice", selected_invoice);
  if (selected_invoice) {
    router.push({ name: "InvoicesView", params: { id: selected_invoice.id } });
  }
  // router.push({ name: "InvoiceSubscription", params: { id } });
};

onMounted(async () => {
  try {
    store.APP_BACK_BUTTON = true;
    store.APP_LOADING = true;
    user.value = store.user;
    if (store.services && store.services.length > 0) {
      serviceData.value =
        store.services.find((service) => service.id === Number(id)) ||
        (await getService(id));
    } else {
      serviceData.value = await getService(id);
    }
    // Give the page a heading with a back button (the router leaves it empty)
    store.I118N_TITLE = false;
    store.APP_TITLE = planName.value || t("invoices.plan");
    if (store.invoices && store.invoices.length > 0) {
      invoiceData.value = store.invoices.filter((invoice) => {
        return (
          invoice.servicio_id !== false && invoice.servicio_id[0] === Number(id)
        );
      });
    } else {
      invoiceData.value = await getInvoicesByService(id);
    }
    await filterInvoices();
    countInvoicesToPay.value = await getCountInvoicesPendingByService(id);
  } catch (error) {
    console.log(error);
  } finally {
    store.APP_LOADING = false;
  }
});
</script>

<template lang="pug">
.plan-details
  AppCard.plan-details__summary(padding="lg" elevated)
    .summary
      .summary__head
        .summary__icon(:class="serviceVariant")
          i.uil(:class="serviceIcon" aria-hidden="true")
        .summary__heading
          .summary__label {{ $t("plan.contract") }}
          .summary__contract {{ contractNumber }}
        span.summary__status(:class="serviceVariant") {{ serviceStatusLabel }}
      .summary__pending(v-if="pendingInvoices.length")
        .summary__pending-amount
          h4 {{ $t("invoices.summary_amount") }}
          strong {{ numberFormatter(pendingAmount) }}
        span.summary__pending-count {{ $t("invoices.pending_count", { count: pendingInvoices.length }) }}
      .summary__actions(v-if="countInvoicesToPay > 0")
        AppButton(:label="$t('payment.subscription.button_action')" color="primary" @click="goInvoiceSubscription()")
        AppButton(:label="$t('login.quick') + ' (' + countInvoicesToPay + ')'" color="primary" @click="goQuickPayment()")

  .plan-details__section
    h2.plan-details__title {{ $t("invoices.title") }}
    .filters(role="tablist" :aria-label="$t('invoices.filter_by')")
      button.filters__tab(
        v-for="item in invoiceTypes"
        :key="item.value"
        type="button"
        role="tab"
        :aria-selected="typeInvoice === item.value"
        :class="{ 'is-active': typeInvoice === item.value }"
        @click="selectType(item.value)"
      )
        i(:class="item.icon" aria-hidden="true")
        span.filters__label {{ item.label }}
        span.filters__count {{ item.count }}

  .cards(v-if="listInvoices.length")
    AppInvoice(v-for="item in listInvoices" :key="item.id" :invoice="item")
  AppCard.plan-details__empty(v-else padding="lg")
    i.uil.uil-file-check-alt(aria-hidden="true")
    p {{ $t("invoices.message_empty") }}
</template>

<style lang="scss" scoped>
.plan-details {
  @apply pb-6;

  &__summary {
    @apply mb-6;
  }

  &__section {
    @apply flex;
    @apply flex-col;
    @apply gap-3;
    @apply mb-4;
    @screen sm {
      @apply flex-row;
      @apply items-center;
      @apply justify-between;
    }
  }

  &__title {
    @apply font-semibold;
    @apply text-bluegray-600;
    @apply text-base;
  }

  &__empty {
    @apply flex;
    @apply flex-col;
    @apply items-center;
    @apply text-center;

    i {
      @apply text-4xl;
      @apply text-bluegray-300;
      @apply mb-3;
    }

    p {
      @apply text-sm;
      @apply text-bluegray-400;
    }
  }
}

.summary {
  &__head {
    @apply flex;
    @apply items-center;
    @apply gap-3;
  }

  &__icon {
    @apply flex;
    @apply flex-shrink-0;
    @apply items-center;
    @apply justify-center;
    @apply w-11;
    @apply h-11;
    @apply rounded-xl;

    i {
      @apply text-2xl;
      @apply leading-none;
    }

    &.success {
      @apply bg-gignetgreen-200;
      i {
        @apply text-gignetgreen-700;
      }
    }
    &.warning {
      @apply bg-yellow-100;
      i {
        @apply text-yellow-600;
      }
    }
    &.danger {
      @apply bg-red-100;
      i {
        @apply text-red-500;
      }
    }
    &.neutral {
      @apply bg-bluegray-100;
      i {
        @apply text-bluegray-500;
      }
    }
  }

  &__heading {
    @apply flex-1;
    @apply min-w-0;
  }

  &__label {
    @apply text-xs;
    @apply text-bluegray-400;
    @apply uppercase;
    @apply tracking-wide;
  }

  &__contract {
    @apply text-bluegray-700;
    @apply font-semibold;
    @apply truncate;
  }

  &__status {
    @apply flex-shrink-0;
    @apply px-2.5;
    @apply py-1;
    @apply rounded-full;
    @apply text-xs;
    @apply font-semibold;
    @apply leading-none;
    @apply whitespace-nowrap;

    &.success {
      @apply bg-gignetgreen-200;
      @apply text-gignetgreen-800;
    }
    &.warning {
      @apply bg-yellow-100;
      @apply text-yellow-700;
    }
    &.danger {
      @apply bg-red-100;
      @apply text-red-600;
    }
    &.neutral {
      @apply bg-bluegray-100;
      @apply text-bluegray-500;
    }
  }

  &__pending {
    @apply flex;
    @apply items-end;
    @apply justify-between;
    @apply gap-3;
    @apply mt-4;
    @apply pt-4;
    @apply border-t;
    @apply border-bluegray-100;

    h4 {
      @apply text-xs;
      @apply font-medium;
      @apply text-bluegray-400;
      @apply uppercase;
      @apply tracking-wide;
    }

    strong {
      @apply block;
      @apply text-2xl;
      @apply font-semibold;
      @apply text-bluegray-700;
      @apply leading-tight;
      font-variant-numeric: tabular-nums;
    }
  }

  &__pending-count {
    @apply flex-shrink-0;
    @apply text-xs;
    @apply text-bluegray-500;
  }

  &__actions {
    @apply flex;
    @apply flex-col;
    @apply gap-3;
    @apply mt-4;
    // Only side by side once the sidebar no longer squeezes the content
    @screen lg {
      @apply flex-row;
      @apply justify-end;
    }
  }
}

.cards {
  @apply grid;
  @apply grid-cols-1;
  @apply gap-4;
  @apply items-stretch;
  @screen lg {
    @apply grid-cols-2;
  }
  @screen 2xl {
    @apply grid-cols-3;
  }
}
</style>
